import { useState } from "react";
import { CalendarPlus, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SpotChecksPanel from "@/components/SpotChecksPanel";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function SpotChecks() {
  const { hasPermission } = useAuth();
  const canSchedule = hasPermission("spotchecks:schedule");
  const [dialog, setDialog] = useState(false);
  const [staff, setStaff] = useState([]);
  const [children, setChildren] = useState([]);
  const [form, setForm] = useState({ staff_id: "", child_id: "", scheduled_date: "" });
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const openDialog = async () => {
    setForm({ staff_id: "", child_id: "", scheduled_date: "" });
    setDialog(true);
    try {
      if (!staff.length) {
        const { data } = await api.get("/staff");
        setStaff((data.items || data || []).filter((s) => s.status === "active"));
      }
      if (!children.length) {
        const { data } = await api.get("/children");
        setChildren(data.items || data || []);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/spot-checks", {
        staff_id: form.staff_id,
        child_id: form.child_id || null,
        scheduled_date: form.scheduled_date,
      });
      toast.success("Spot check scheduled — the Co-ordinator has been notified");
      setDialog(false);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-4xl mx-auto" data-testid="spot-checks-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Spot Checks</h1>
          <p className="text-sm text-slate-500 mt-1">
            Unannounced quality checks on trainers and live sessions. Programmed by the Manager, carried out and recorded by the Co-ordinator.
          </p>
        </div>
        {canSchedule && (
          <Button data-testid="spotcheck-schedule-button" onClick={openDialog}
            className="bg-primary hover:bg-brand-hover text-white font-semibold">
            <CalendarPlus className="h-4 w-4 mr-2" /> Schedule spot check
          </Button>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Timetable &amp; records</CardTitle></CardHeader>
        <CardContent>
          <SpotChecksPanel key={reloadKey} />
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-white" data-testid="spotcheck-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule a spot check</DialogTitle>
            <DialogDescription>
              Programme an unannounced check on a staff member. Link a child when the check should happen during their session.
              The Co-ordinator is notified and records the outcome.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Staff member</Label>
              <Select value={form.staff_id} onValueChange={(v) => setForm((f) => ({ ...f, staff_id: v }))}>
                <SelectTrigger data-testid="spotcheck-staff-select" className="h-11"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`spotcheck-staff-option-${s.id}`}>
                      {s.name}{s.staff_number ? ` (${s.staff_number})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Child (optional)</Label>
              <Select value={form.child_id} onValueChange={(v) => setForm((f) => ({ ...f, child_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="spotcheck-child-select" className="h-11"><SelectValue placeholder="Not linked to a child" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none" data-testid="spotcheck-child-option-none">Not linked to a child</SelectItem>
                  {children.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`spotcheck-child-option-${c.id}`}>
                      {c.first_name} {c.surname}{c.case_id ? ` (${c.case_id})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="spotcheck-date">Date</Label>
              <Input id="spotcheck-date" data-testid="spotcheck-date-input" type="date" className="h-11"
                value={form.scheduled_date} onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="spotcheck-save-button" disabled={saving || !form.staff_id || !form.scheduled_date} onClick={save}
              className="bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
